import React, { useEffect, useRef } from 'react';
import {
  Alert, AlertIcon, Box, Button, Text, useToast,
} from '@chakra-ui/react';

// item 1502 (Chad) — NEW-VERSION TOAST. A tab left open across a deploy keeps running the old
// bundle: its lazy chunks 404 (item 1515 reloads once for that) and its API calls can drift
// from the new contract. Every 5 min, and whenever the tab comes back to the foreground, fetch
// the build's asset-manifest (no-store, cache-busted) and compare its main.js against the
// <script> this page actually loaded. On a mismatch show ONE persistent toast with a Reload
// button — NEVER auto-reload (people are mid-form). Production only; every failure is swallowed.
const CHECK_EVERY_MS = 5 * 60 * 1000;
const TOAST_ID = 'new-version';

const basename = (path: string) => path.split('?')[0].split('/').pop() ?? '';

// the main bundle THIS tab is running (CRA injects <script src="/static/js/main.<hash>.chunk.js">)
const loadedMainJs = (): string | null => {
  const script = document.querySelector<HTMLScriptElement>('script[src*="/static/js/main."]');
  return script?.src ? basename(script.src) : null;
};

const latestMainJs = async (): Promise<string | null> => {
  const res = await fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const manifest = await res.json();
  const main = manifest?.files?.['main.js'];
  return typeof main === 'string' && main ? basename(main) : null;
};

const NewVersionToast: React.FC = () => React.createElement(
  Alert,
  {
    status: 'info', variant: 'solid', borderRadius: 'md', alignItems: 'center',
  },
  React.createElement(AlertIcon),
  React.createElement(
    Box,
    { flex: '1', mr: 3 },
    React.createElement(Text, { fontWeight: 'semibold', fontSize: 'sm' }, 'A new version of Basix is live'),
    React.createElement(Text, { fontSize: 'xs' }, 'Reload when you’re at a stopping point.'),
  ),
  React.createElement(
    Button,
    { size: 'sm', colorScheme: 'whiteAlpha', onClick: () => window.location.reload() },
    'Reload',
  ),
);

const useNewVersionCheck = () => {
  const toast = useToast();
  const shownRef = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return undefined;
    const loaded = loadedMainJs();
    if (!loaded) return undefined; // not a CRA build we can compare (dev server / unexpected markup)

    let disposed = false;
    const check = async () => {
      if (disposed || shownRef.current) return;
      if (document.visibilityState !== 'visible' || navigator.onLine === false) return;
      try {
        const latest = await latestMainJs();
        if (disposed || shownRef.current || !latest || latest === loaded) return;
        shownRef.current = true; // ONE toast per tab, never re-toasted
        if (toast.isActive(TOAST_ID)) return;
        toast({
          id: TOAST_ID,
          status: 'info',
          duration: null,
          isClosable: false,
          position: 'bottom-right',
          render: () => React.createElement(NewVersionToast),
        });
      } catch {
        /* offline / proxy hiccup / non-JSON — try again next tick */
      }
    };

    const timer = window.setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [toast]);
};

export default useNewVersionCheck;
